/**
 * This file represents an alternative pattern when using Zustand's `persist` middleware.
 * In this case, we include actions within the store itself, as `persist` handles state persistence
 * across sessions. Unlike our standard approach where actions are externalized, here we centralize
 * state and actions for ease of persistence management.
 *
 * The `persist` middleware automatically saves the `token` to localStorage under the key "authStore".
 * We only store the `token` here to comply with HIPAA regulations, ensuring that no sensitive user information
 * beyond authentication tokens is persisted.
 *
 * This pattern is specifically used when persistence is required, overriding our standard practice of separating state and actions.
 */

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { LoginStep } from "~/screens/Auth/Login/constants";
import { safeStorage } from "~/shared/utils/safeStorage";

export interface AuthStoreState {
  loginStep: number;
  setLoginStep(step: number): void;
  authEmail: string;
  setAuthEmail(email: string): void;

  accessToken: string | null;
  refreshToken: string | null;
  redirectTo: string | null;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setAccessToken: (accessToken: string) => void;
  setRefreshToken: (refreshToken: string) => void;
  setRedirectTo: (redirectTo: string | null) => void;
  clearStore: () => void;
  isLoggedIn: () => boolean;
}

export const useAuthStore = create<AuthStoreState>()(
  persist(
    (set, get) => ({
      authEmail: "",
      loginStep: 1,
      setLoginStep: (step: LoginStep) => {
        set(() => ({ loginStep: step }));
      },
      setAuthEmail: (email: string) => {
        set(() => ({ authEmail: email }));
      },

      accessToken: null,
      refreshToken: null,
      redirectTo: null,
      setTokens: (accessToken: string, refreshToken: string) => {
        set(() => ({ accessToken, refreshToken }));
      },
      setAccessToken: (accessToken: string) => {
        set(() => ({ accessToken }));
      },
      setRefreshToken: (refreshToken: string) => {
        set(() => ({ refreshToken }));
      },
      setRedirectTo: (redirectTo: string | null) => {
        set(() => ({ redirectTo }));
      },
      clearStore: () => {
        set(() => ({
          accessToken: null,
          refreshToken: null,
          redirectTo: null,
          authEmail: "",
          loginStep: 1,
        }));
      },
      isLoggedIn: () => !!get().accessToken,
    }),
    {
      name: "authStore",
      storage: createJSONStorage(() => safeStorage),
      onRehydrateStorage: () => (state) => {
        if (state && !state.accessToken && !state.refreshToken) {
          const userStoreData = safeStorage.getItem("userStore");

          if (userStoreData) {
            const data = JSON.parse(userStoreData);

            if (data?.state?.role) {
              safeStorage.removeItem("userStore");
            }
          }
        }
      },
    },
  ),
);
